import React, { useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Switch,
  IconButton,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DeleteIcon from "@mui/icons-material/Delete";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useGetLocationsQuery } from "slices/common/locationSlice";
import { useNavigate, useParams } from "react-router-dom";
import {
  useAddTimeslotMutation,
  useUpdateTimeslotMutation,
  useGetSingleTimeslotQuery,
} from "slices/common/timeslotSlice";

export default function AddTimeslot() {
  const navigate = useNavigate();
   const { id, locId } = useParams(); // ✅ get both route params

  const isEdit = Boolean(id);

  const [addTimeslot] = useAddTimeslotMutation();
  const [updateTimeslot] = useUpdateTimeslotMutation();
  const { data: locations } = useGetLocationsQuery({});
  const { data: initialData, isLoading } = useGetSingleTimeslotQuery(
    { id, locId }, // send both
    { skip: !isEdit }
  );
  const formik = useFormik({
    initialValues: {
      location: "",
      day: "",
      slots: [],
    },
    validationSchema: Yup.object().shape({
      location: Yup.string().required("Please select location"),
      day: Yup.string().required("Please select day"),
      slots: Yup.array()
        .of(
          Yup.object().shape({
            start: Yup.string().when("active", {
              is: true,
              then: (schema) => schema.required("Start time is required"),
              otherwise: (schema) => schema.notRequired(),
            }),
            end: Yup.string().when("active", {
              is: true,
              then: (schema) => schema.required("End time is required"),
              otherwise: (schema) => schema.notRequired(),
            }),
            active: Yup.boolean().required("Active field is required"),
          })
        )
        .min(1, "At least one slot is required"),
    }),
    onSubmit: async (values) => {
      const payload = {
        ...values,
        slots: values.slots.filter((s) => s.start && s.end),
      };

      console.log("payload", payload);
      try {
        if (isEdit) {
          await updateTimeslot({ id: payload._id, body: payload });

          navigate("/settings/timeslots");
        } else {
          await addTimeslot(payload);
          navigate("/settings/timeslots");
        }
      } catch (err) {
        console.error("Error saving timeslot:", err);
      }
    },
  });

  useEffect(() => {
    if (isEdit && initialData?.data) {
      const { day, slots } = initialData.data;
      formik.setValues({
        _id: initialData?.data?._id || "",
        location: initialData?.data?.location?._id || "",
        day: day || "",
        slots: Array.isArray(slots)
          ? slots.map((s) => ({
              start: s.start || "",
              end: s.end || "",
              active: s.active ?? true,
              _id: s._id ?? "",
            }))
          : [],
      });
    }
  }, [initialData]);

  const handleAddSlot = () => {
    formik.setFieldValue("slots", [
      ...formik.values.slots,
      { start: "", end: "", active: true },
    ]);
  };

  const handleSlotChange = (index, field, value) => {
    const updated = [...formik.values.slots];
    updated[index][field] = value;
    formik.setFieldValue("slots", updated);
  };

  const handleDeleteSlot = (index) => {
    const updated = [...formik.values.slots];
    updated.splice(index, 1);
    formik.setFieldValue("slots", updated);
  };

  const handleToggleActive = (index) => {
    const updated = [...formik.values.slots];
    updated[index].active = !updated[index].active;
    formik.setFieldValue("slots", updated);
  };

  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }} component="form" onSubmit={formik.handleSubmit}>
          <Typography variant="h5" gutterBottom>
            {isEdit ? "Edit Timeslot" : "Add Timeslot"}
          </Typography>

          {/* Location */}
       <FormControl fullWidth margin="normal">
            <InputLabel id="location-label">Location</InputLabel>
            <Select
              labelId="location-label"
              name="location"
              value={formik.values.location}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Location"
            >
              {locations?.data?.data?.map((loc) => (
                <MenuItem key={loc._id} value={loc._id}>
                  {loc.cityName}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.location && formik.errors.location && (
              <Typography variant="caption" color="error">
                {formik.errors.location}
              </Typography>
            )}
          </FormControl> 
          {/* <FormControl fullWidth margin="normal"> */}
            {/* <label htmlFor="location">Location</label>
            <select
              id="location"
              name="location"
              value={formik.values.location} // 👈 stores only the ID
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              style={{ padding: "8px", borderRadius: "4px", width: "100%" }}
            >
              {locations?.data?.data?.map((loc) => (
                <option key={loc._id} value={loc._id} selected={formik.values.location == loc._id}>
                  {loc.cityName} 
                </option>
              ))}
            </select>

            {formik.touched.location && formik.errors.location && (
              <Typography variant="caption" color="error">
                {formik.errors.location}
              </Typography>
            )} */}
          {/* </FormControl> */}
 
          {/* Day */}
          <FormControl fullWidth margin="normal">
            <InputLabel id="day-label">Day</InputLabel>
            <Select
              labelId="day-label"
              name="day"
              value={formik.values.day}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Day"
            >
              {days.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.day && formik.errors.day && (
              <Typography variant="caption" color="error">
                {formik.errors.day}
              </Typography>
            )}
          </FormControl>

          {/* Time Slots */}
          {formik.values.slots.map((slot, i) => (
            <Box display="flex" gap={1} alignItems="center" key={i} mt={2}>
              <TextField
                label="Start Time"
                type="time"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={slot.start}
                onChange={(e) => handleSlotChange(i, "start", e.target.value)}
                onBlur={() => formik.setFieldTouched(`slots[${i}].start`, true)}
                error={
                  formik.touched.slots?.[i]?.start &&
                  Boolean(formik.errors.slots?.[i]?.start)
                }
                helperText={
                  formik.touched.slots?.[i]?.start &&
                  formik.errors.slots?.[i]?.start
                }
              />
              <TextField
                label="End Time"
                type="time"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={slot.end}
                onChange={(e) => handleSlotChange(i, "end", e.target.value)}
                onBlur={() => formik.setFieldTouched(`slots[${i}].end`, true)}
                error={
                  formik.touched.slots?.[i]?.end &&
                  Boolean(formik.errors.slots?.[i]?.end)
                }
                helperText={
                  formik.touched.slots?.[i]?.end &&
                  formik.errors.slots?.[i]?.end
                }
              />
              <Tooltip title={slot.active ? "Active" : "Inactive"}>
                <Switch
                  size="small"
                  checked={slot.active}
                  onChange={() => handleToggleActive(i)}
                />
              </Tooltip>
              <IconButton onClick={() => handleDeleteSlot(i)}>
                <DeleteIcon sx={{ color: "red" }} />
              </IconButton>
            </Box>
          ))}

          <Button onClick={handleAddSlot} sx={{ mt: 2 }} variant="outlined">
            + Add Slot
          </Button>

          <Box mt={3} textAlign="right">
            <Button type="submit" variant="contained">
              {isEdit ? "Update" : "Submit"}
            </Button>
          </Box>
        </Paper>
      </Container>
    </DashboardLayout>
  );
}
